/**
 * SignupPage - Split-screen registration page.
 *
 * Layout:
 * - Left column (40%): Form with fields (Nome, Sobrenome, Email, Celular, Senha, Confirmar Senha)
 * - Right column (60%): Branding panel with logo and motivational text
 *
 * On mobile: single column (form only, branding hidden).
 * After successful signup: redirects to /onboarding.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Turnstile from '@/components/Turnstile';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { isPasswordValid, validatePassword } from '@/lib/passwordPolicy';

// Signup is gated by Cloudflare Turnstile when VITE_TURNSTILE_SITE_KEY is set.
// In local dev where it's empty, the widget renders nothing and the form
// submits without a token — the backend mirrors this behaviour.
const TURNSTILE_ENABLED = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

// ============================================================================
// PHONE MASK HELPER
// ============================================================================

/**
 * Formats a phone number string into Brazilian format: (XX) XXXXX-XXXX
 */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { signUp, user } = useAuth();
  const flow = searchParams.get('flow') || '';

  // Track whether the user just signed up (to avoid redirect race condition)
  const justSignedUpRef = useRef(false);

  // Redirect if already logged in (but not if we just completed signup)
  useEffect(() => {
    if (user && !justSignedUpRef.current) navigate('/', { replace: true });
  }, [user, navigate]);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Ensure light mode on signup page (split-screen layout looks better in light)
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    return () => {
      // Restore dark mode when leaving (will be set by the app later if needed)
    };
  }, []);

  const passwordValidation = validatePassword(password);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!firstName.trim()) errors.firstName = t('signup.errors.firstNameRequired');
    if (!lastName.trim()) errors.lastName = t('signup.errors.lastNameRequired');
    if (!email.trim()) errors.email = t('signup.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t('signup.errors.emailInvalid');
    if (!phone.trim()) errors.phone = t('signup.errors.phoneRequired');
    else if (phone.replace(/\D/g, '').length < 10) errors.phone = t('signup.errors.phoneInvalid');
    if (!password) errors.password = t('signup.errors.passwordRequired');
    else if (!isPasswordValid(passwordValidation)) errors.password = t('signup.errors.passwordWeak');
    if (!confirmPassword) errors.confirmPassword = t('signup.errors.confirmPasswordRequired');
    else if (password !== confirmPassword) errors.confirmPassword = t('signup.errors.passwordMismatch');

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    // When Turnstile is enabled, block submission until the widget returns a
    // token. This prevents accidental double-submits before the challenge
    // completes and also blocks bots that don't execute the JS challenge.
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError(t('signup.errors.captchaRequired'));
      return;
    }

    setLoading(true);
    justSignedUpRef.current = true;
    try {
      const { error: signUpError } = await signUp(email, password, firstName, lastName, phone, turnstileToken);
      if (signUpError) {
        justSignedUpRef.current = false;
        setError(signUpError.message.startsWith('auth.') ? t(signUpError.message) : signUpError.message);
      } else {
        // Persist flow to sessionStorage so it survives ProtectedRoute redirect cycles
        if (flow) {
          try { sessionStorage.setItem('cai_onboarding_flow', flow); } catch { /* ignore */ }
        }
        const onboardingPath = flow ? `/onboarding?flow=${encodeURIComponent(flow)}` : '/onboarding';
        navigate(onboardingPath, { replace: true });
      }
    } catch {
      justSignedUpRef.current = false;
      setError(t('signup.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Form */}
      <div className="w-full lg:w-[40%] flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-8 bg-white overflow-y-auto">
        <div className="max-w-md mx-auto w-full space-y-6">
          {/* Logo + Headline */}
          <div className="flex flex-col items-center gap-0">
            <img src="/logo-carousel-register.png" alt="Core Carousel" style={{ width: 200, height: 'auto', marginBottom: -50 }} />
            <p className="text-sm text-muted-foreground leading-relaxed text-center">
              {t('signup.headline')}{' '}
              <span className="font-bold text-foreground">{t('signup.headlineBold')}</span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* First Name */}
            <div>
              <Input
                type="text"
                placeholder={t('signup.firstName') + ' *'}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                className={fieldErrors.firstName ? 'border-destructive' : ''}
              />
              {fieldErrors.firstName && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <Input
                type="text"
                placeholder={t('signup.lastName') + ' *'}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                className={fieldErrors.lastName ? 'border-destructive' : ''}
              />
              {fieldErrors.lastName && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.lastName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <Input
                type="email"
                placeholder={t('signup.email') + ' *'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className={fieldErrors.email ? 'border-destructive' : ''}
              />
              {fieldErrors.email && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Input
                type="tel"
                placeholder={t('signup.phone') + ' *'}
                value={phone}
                onChange={handlePhoneChange}
                disabled={loading}
                className={fieldErrors.phone ? 'border-destructive' : ''}
              />
              {fieldErrors.phone && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.phone}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('signup.password') + ' *'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className={fieldErrors.password ? 'border-destructive pr-10' : 'pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password && !isPasswordValid(passwordValidation) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('signup.passwordHint')}
                </p>
              )}
              {fieldErrors.password && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('signup.confirmPassword') + ' *'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className={fieldErrors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="text-xs text-destructive mt-1">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            {/* Cloudflare Turnstile (invisible CAPTCHA) */}
            {TURNSTILE_ENABLED && (
              <div className="flex justify-center">
                <Turnstile onVerify={setTurnstileToken} action="signup" theme="light" />
              </div>
            )}

            {/* Global Error */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Submit */}
            <Button type="submit" className="w-full h-11" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('signup.creating')}
                </>
              ) : (
                t('signup.submit')
              )}
            </Button>
          </form>

          {/* Help Link */}
          <p className="text-center text-xs text-primary">
            <a href="mailto:suporte@corecarousel.com" className="hover:underline">
              {t('signup.helpLink')}
            </a>
          </p>

          {/* Already have account */}
          <p className="text-center text-sm text-muted-foreground">
            {t('signup.hasAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t('signup.loginLink')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Column - Branding (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-[60%] items-center justify-center p-12"
        style={{ background: 'linear-gradient(135deg, #030513 0%, #0a1628 40%, #0f2027 70%, #030513 100%)' }}
      >
        <div className="text-center space-y-8 max-w-lg">
          <div className="flex justify-center">
            <div className="p-6 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(71,99,202,0.15) 0%, rgba(71,99,202,0.05) 100%)', border: '1px solid rgba(71,99,202,0.2)' }}>
              <CoreStudioMark size={80} />
            </div>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
            {t('signup.brandingTitle')}{' '}
            <span className="text-primary">{t('signup.brandingHighlight')}</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('signup.brandingSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
